import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, FileSpreadsheet, Loader2, Plus, Ruler, TriangleAlert, Upload, X } from "lucide-react";
import { MAX_PIECES, optimizeCuts } from "@/lib/cutOptimizer";
import { scrollToId } from "@/lib/scroll";
import { track } from "@/lib/track";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, d = 0) => n.toLocaleString("it-IT", { maximumFractionDigits: d, minimumFractionDigits: d });
const MAX_ROWS = 20;

// Beam list: rows of (quantity, length) entered by the designer (empty length = span), total timber
// volume and a cutting plan from standard 12.00 / 13.50 m beams (1 cm per cut, ends trimmed).
export const beamList = (p, r, f) => {
  const rows = f.rows.map((x) => ({ n: Math.max(0, Math.floor(+x.n || 0)), len: +x.len > 0 ? +x.len : p.L })).filter((x) => x.n >= 1 && x.len > 0);
  const count = rows.reduce((s, x) => s + x.n, 0);
  const ok = rows.length > 0 && count <= MAX_PIECES;
  const cuts = ok ? optimizeCuts(rows) : null;
  const sec = r ? (p.b / 100) * (r.h / 100) : 0;
  const vol = rows.reduce((s, x) => s + x.n * x.len, 0) * sec;
  return { rows, count, ok, cuts, vol, volOrder: cuts ? cuts.metres * sec : 0 };
};

const RowInputs = ({ row, i, f, set, t, span }) => {
  const upd = (k) => (e) => set("rows")(f.rows.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
  return (
    <div className="flex items-end gap-3" data-testid={`calc-row-${i}`}>
      <label className="block w-24 shrink-0">
        <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#A39E93]">{t("cuts.n", "Numero di travi")}</span>
        <input type="number" min="1" step="1" value={row.n} onChange={upd("n")} className="field" data-testid={`calc-row-${i}-n`} />
      </label>
      <label className="block min-w-0 flex-1">
        <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#A39E93]">{t("cuts.len", "Lunghezza di ogni trave (m)")}</span>
        <input type="number" min="0" step="0.05" value={row.len} placeholder={fmt(span, 2)} onChange={upd("len")} className="field" data-testid={`calc-row-${i}-len`} />
      </label>
      {f.rows.length > 1 && (
        <button onClick={() => set("rows")(f.rows.filter((_, j) => j !== i))} aria-label={t("cuts.remove", "Rimuovi riga")} className="tap mb-2 rounded-full border border-white/20 p-2 text-[#D6D0C4] transition-colors duration-300 hover:border-[#D98A52] hover:text-[#D98A52]" data-testid={`calc-row-${i}-remove`}>
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

const Totals = ({ p, r, list, t }) => (
  <div className="grid gap-6 sm:grid-cols-2">
    <div data-testid="calc-cuts-volume">
      <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#A39E93]">{t("cuts.vol", "Volume totale")}</p>
      <p className="mt-2 font-display text-3xl font-bold tabular-nums">{r ? `${fmt(list.vol, 3)} m\u00b3` : "\u2014"}</p>
      <p className="text-xs text-[#A39E93]">{r ? `${list.count} ${t("cuts.beams", "travi")} · ${p.b} × ${r.h} cm` : ""}</p>
    </div>
    <div data-testid="calc-cuts-order">
      <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#A39E93]">{t("cuts.order", "Da ordinare")}</p>
      <p className="mt-2 font-display text-3xl font-bold tabular-nums">{list.cuts?.bars ? `${list.cuts.bars} × ` : "\u2014"}<span className="text-lg">{list.cuts?.bars ? t("cuts.std", "travi standard") : ""}</span></p>
      {list.cuts?.bars > 0 && <p className="text-xs text-[#A39E93]">{fmt(list.cuts.metres, 2)} m · {r ? `${fmt(list.volOrder, 3)} m\u00b3 · ` : ""}{t("cuts.waste", "sfrido")} {fmt(list.cuts.waste, 2)} m ({fmt(list.cuts.wastePct, 1)}%)</p>}
    </div>
  </div>
);

const Plan = ({ cuts, t }) => (
  <ul className="mt-6 space-y-2 text-sm" data-testid="calc-cuts-plan">
    {cuts.groups.map((g, i) => (
      <li key={g.key} className="border-b border-white/10 pb-2" data-testid={`calc-cuts-group-${i}`}>
        <b className="tabular-nums">{g.count} × {fmt(g.stock, 2)} m</b> <span className="text-[#D6D0C4]">→ {g.pieces.map((l) => fmt(l, 2)).join(" + ")} m · {t("cuts.waste", "sfrido")} {fmt(g.waste, 2)} m</span>
      </li>
    ))}
  </ul>
);

const quoteMessage = (p, r, list, t) => {
  const beams = list.rows.map((x) => `${x.n} × ${fmt(x.len, 2)} m`).join(" + ");
  const plan = list.cuts?.groups.map((g) => `${g.count} × ${fmt(g.stock, 2)} m (${g.pieces.map((l) => fmt(l, 2)).join(" + ")})`).join(", ") || "\u2014";
  const over = list.cuts?.over.length ? ` ${t("cuts.overShort", "Fuori standard, su richiesta")}: ${list.cuts.over.map((l) => fmt(l, 2)).join(", ")} m.` : "";
  return t("cuts.msg2", "Vorrei un preventivo per travi in legno lamellare {grade} {b} × {h} cm: {beams}. Volume totale {v} m³. Taglio da travi standard: {plan}.")
    .replace("{grade}", p.grade).replace("{b}", p.b).replace("{h}", r?.h ?? "\u2014").replace("{beams}", beams)
    .replace("{v}", fmt(list.vol, 3)).replace("{plan}", plan) + over;
};

// Beam list from an Excel/CSV file (columns Quantità + Lunghezza), read by the backend
const FileImport = ({ set, t }) => {
  const input = useRef(null);
  const { lang } = useLang();
  const [busy, setBusy] = useState(false);
  const load = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await axios.post(`${API}/calc-rows/parse`, body);
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      set("rows")(rows.map((x) => ({ n: String(x.n), len: String(x.len) })));
      track("import_distinta", { righe: rows.length });
      toast.success(t("cuts.loaded", "Distinta caricata: {r} lunghezze, {n} travi.").replace("{r}", rows.length).replace("{n}", data?.beams ?? 0));
    } catch (err) {
      toast.error(lang === "it" && typeof err?.response?.data?.detail === "string" ? err.response.data.detail : t("cuts.loadErr", "File non leggibile: usa un file .xlsx o .csv con le colonne Quantità e Lunghezza."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-white/10 pt-4" data-testid="calc-import">
      <input ref={input} type="file" accept=".xlsx,.csv" onChange={load} className="hidden" data-testid="calc-import-input" />
      <button onClick={() => input.current?.click()} disabled={busy} className="tap inline-flex items-center gap-2 rounded-full border border-[#D98A52] px-4 py-2 text-sm transition-colors duration-300 hover:bg-[#D98A52]/15 disabled:opacity-60" data-testid="calc-import-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 text-[#D98A52]" />} {t("cuts.import", "Carica da Excel o CSV")}
      </button>
      <a href={`${API}/calc-rows/template`} className="inline-flex items-center gap-2 text-sm text-[#D6D0C4] underline-offset-4 transition-colors duration-300 hover:text-[#D98A52] hover:underline" data-testid="calc-import-template">
        <FileSpreadsheet className="h-4 w-4" /> {t("cuts.template", "Scarica il modello Excel")}
      </a>
      <p className="w-full text-xs text-[#A39E93]">{t("cuts.importHint", "Colonne Quantità e Lunghezza (in m, o in mm/cm se indicato nell'intestazione). Le lunghezze uguali vengono unite; il caricamento sostituisce le righe attuali.")}</p>
    </div>
  );
};

export const CalcCuts = ({ p, r, f, set, t, onXlsx, xlsxBusy }) => {
  const list = beamList(p, r, f);
  const ask = () => {
    window.dispatchEvent(new CustomEvent("prefill-quote", { detail: { project_type: "Legno lamellare BSH", message: quoteMessage(p, r, list, t) } }));
    scrollToId("#contatti");
  };
  return (
    <div className="mt-10 border border-white/15 p-6 sm:p-8" data-testid="calc-cuts">
      <p className="flex items-center gap-2 font-display text-xl font-bold"><Ruler className="h-5 w-5 text-[#D98A52]" /> {t("cuts.h", "Distinta travi e ottimizzazione lunghezze")}</p>
      <p className="mt-2 max-w-3xl text-sm text-[#D6D0C4]">{t("cuts.p2", "Inserisci le travi che servono, anche di lunghezze diverse: calcoliamo il volume totale e come ricavarle insieme dalle travi standard da 12,00 e 13,50 m con il minimo sfrido.")}</p>
      <div className="mt-6 grid gap-10 lg:grid-cols-2">
        <div className="space-y-4">
          {f.rows.map((row, i) => <RowInputs key={i} row={row} i={i} f={f} set={set} t={t} span={p.L} />)}
          {f.rows.length < MAX_ROWS && (
            <button onClick={() => set("rows")([...f.rows, { n: "1", len: "" }])} className="tap inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-[#D6D0C4] transition-colors duration-300 hover:border-[#D98A52]" data-testid="calc-row-add">
              <Plus className="h-4 w-4 text-[#D98A52]" /> {t("cuts.add", "Aggiungi una lunghezza")}
            </button>
          )}
          {f.rows.some((x) => !x.len) && <p className="text-xs text-[#A39E93]">{t("cuts.lenHint", "Lunghezza non inserita: usiamo la luce. Aggiungi gli appoggi o gli sbalzi per una distinta precisa.")}</p>}
          <FileImport set={set} t={t} />
        </div>
        <Totals p={p} r={r} list={list} t={t} />
      </div>
      {list.cuts?.over.length > 0 && <p className="mt-6 flex items-start gap-2 text-sm text-[#E9A06B]" data-testid="calc-cuts-over"><TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" /> {t("cuts.over2", "Lunghezze fuori standard (oltre 13,48 m): {l} m. Disponibili su richiesta, chiedici un preventivo.").replace("{l}", list.cuts.over.map((l) => fmt(l, 2)).join(", "))}</p>}
      {list.cuts?.groups.length > 0 && <Plan cuts={list.cuts} t={t} />}
      {!list.ok && <p className="mt-6 text-sm text-[#E9A06B]" data-testid="calc-cuts-invalid">{t("cuts.invalid2", "Inserisci almeno una trave con lunghezza maggiore di zero (massimo {m} travi).").replace("{m}", MAX_PIECES)}</p>}
      <p className="mt-3 text-xs text-[#A39E93]">{t("cuts.note2", "Ogni taglio consuma 1 cm, compresa la rifilatura alle due estremità della trave standard: da una trave si ricava al massimo 13,48 m. Le travi da 12,00 e 13,50 m si combinano per ordinare meno metri possibile.")}</p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button onClick={ask} disabled={!list.ok} className="btn-amber disabled:opacity-60" data-testid="calc-cuts-quote">{t("cuts.cta", "Richiedi il preventivo per queste travi")} <ArrowRight className="h-4 w-4" /></button>
        <button onClick={onXlsx} disabled={!list.ok || !list.cuts?.groups.length || xlsxBusy} className="tap inline-flex items-center gap-2 rounded-full border border-[#D98A52] px-5 py-3 text-sm font-semibold transition-colors duration-300 hover:bg-[#D98A52]/15 disabled:opacity-60" data-testid="calc-cuts-xlsx">
          {xlsxBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-[#D98A52]" />} {t("cuts.xlsx", "Scarica il piano in Excel")}
        </button>
      </div>
    </div>
  );
};
